import {  useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useUrlFilters } from '@/hooks/useUrlFilters'

import { Badge, FilterButton, Modal, Pagination, Spinner } from '@/components/ui'

import { fetchContacts, 
  setFilters, 
  contactFilters,
  ordonnedContactFilters,
  archiveContact, restoreContact, deleteContact,
  setCurrentContact, setEmptyCurrentContact
} from './contactsSlice'
import AddContactButton from './AddContactButton'
import ContactForm from './form/ContactForm'
import ContactItem from './ContactItem'

export default () => {
  const dispatch = useDispatch()
  const contacts = useSelector((state) => state.contacts.items)
  const filters = useSelector((state) => state.contacts.filters)
  const pagination = useSelector((state) => state.contacts.pagination)
  const isLoading = useSelector((state) => state.contacts.loadingStatus.fetchList)
  const archiveLoading = useSelector((state) => state.contacts.loadingStatus.archive)
  const restoreLoading = useSelector((state) => state.contacts.loadingStatus.restore)
  const deleteLoading = useSelector((state) => state.contacts.loadingStatus.delete)
  const requestError = useSelector((state) => state.contacts.requestStatus.error)

  const [animatingContactIds, setAnimatingContactIds] = useState([]) // Contacts en animation
  const [pendingActionIds, setPendingActionIds] = useState([]) // Contacts en attente de réponse API

  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  useUrlFilters({
    setFiltersAction: setFilters,
    fetchAction: fetchContacts,
    filters,
    allowedParams: ['type', 'nature', 'page', 'archived', 'limit']
  })

  // Effet pour gérer la fin des requêtes d'archivage/restauration
  useEffect(() => {
    // Si une requête d'archivage/restauration est terminée (plus en chargement)
    if (!archiveLoading && !restoreLoading && !deleteLoading && pendingActionIds.length > 0) {
      // Si pas d'erreur, on continue l'animation jusqu'à la fin
      if (!requestError) {
        // On laisse l'animation se terminer
        setTimeout(() => {
          setAnimatingContactIds(prev => prev.filter(id => !pendingActionIds.includes(id)))
          setPendingActionIds([])
        }, 300) // Un délai plus court car l'animation a déjà commencé
      } else {
        // En cas d'erreur, on annule l'animation immédiatement
        setAnimatingContactIds(prev => prev.filter(id => !pendingActionIds.includes(id)))
        setPendingActionIds([])
      }
    }
  }, [archiveLoading, restoreLoading, deleteLoading, requestError, pendingActionIds])

  const handleGetList = (event) => {
    event.preventDefault()
    // Inverser le filtre "archivés"
    dispatch(setFilters({archived: !filters.archived}))
  }

  const handleEditClick = (contactId) => {
    // Récupérer d'abord le contact
    const contact = contacts.find(c => c.id === contactId)
    
    // Puis définir le contact actuel et ouvrir la modal
    if (contact) {
      dispatch(setCurrentContact(contact))
      handleShow()
    }
  }

  const handleArchiveClick = (contactId) => {
    // Ajouter l'ID du contact à la liste des contacts en cours d'animation
    setAnimatingContactIds(prev => [...prev, contactId])
    // Ajouter l'ID du contact à la liste des actions en attente
    setPendingActionIds(prev => [...prev, contactId])
    
    // Dispatcher l'action immédiatement
    dispatch(filters.archived ? 
      restoreContact(contactId) :
      archiveContact(contactId)
    )
    // La fin de l'animation sera gérée par l'effet ci-dessus
  } 

  const handleDeleteClick = (contactId) => {
    // Ajouter l'ID du contact à la liste des contacts en cours d'animation
    setAnimatingContactIds(prev => [...prev, contactId])
    // Ajouter l'ID du contact à la liste des actions en attente
    setPendingActionIds(prev => [...prev, contactId])

    dispatch(deleteContact(contactId))
  }

  const handlePageChange = (page) => {
    dispatch(setFilters({ page }))
  }

  const handleFiltersChange = (newFilters) => {
    const filterUpdates = {
      // Réinitialiser la pagination
      page: 1
    }
  
    // Vérifier si le filtre 'type' est présent dans newFilters
    if ('type' in newFilters) {
      // Si oui, utiliser la nouvelle valeur
      filterUpdates.type = newFilters.type
    } else {
      // Si non, réinitialiser ce filtre
      filterUpdates.type = undefined
    }
    
    // Vérifier si le filtre 'nature' est présent dans newFilters
    if ('nature' in newFilters) {
      // Si oui, utiliser la nouvelle valeur
      filterUpdates.nature = newFilters.nature
    } else {
      // Si non, réinitialiser ce filtre
      filterUpdates.nature = undefined
    }
    
    // Mettre à jour les filtres dans le state Redux
    dispatch(setFilters(filterUpdates))
  }

  const closeModal = () => {
    dispatch(setEmptyCurrentContact())
    handleHide()
  }

  const getFilterBadgeText = (filterKey) => {
    const value = filters[filterKey]
    if(!value) return null;
    return contactFilters[value] ? contactFilters[value].label.toLowerCase() : null
  }

  return (
    <div>
      <div className='flex items-center justify-between'>
        <div className='mb-4'>
          <h1 className="text-xl font-semibold">
            {filters.archived ? 'Les Contacts archivés' : 'Les Contacts'}
          </h1>
          <div>
            { ['type', 'nature'].map( (filterKey, index) => (
              getFilterBadgeText(filterKey) && 
                <Badge key={index} text={getFilterBadgeText(filterKey)} className='mr-2'/>
            ))}
          </div>
        </div>

        <div className="flex items-right justify-between items-center mb-4">
          {filters.archived ?
            <div className="hover:underline font-bold mr-4">
              <a href="/contacts" onClick={handleGetList} className="text-sm text-gray-600 hover:text-gray-800">
                Retour aux contacts actifs
              </a>
            </div> :
            <div className="hover:underline font-bold mr-4">
              <a href="/contacts?archived=true" onClick={handleGetList} className="text-sm text-gray-600 hover:text-gray-800">
                Voir les contacts archivés
              </a>
            </div>
          }
          <div className="dropdown">
            <FilterButton 
              filters={ordonnedContactFilters(filters)}
              onChange={handleFiltersChange}
            />
          </div>
          
          <div>
            <AddContactButton disabled={filters.archived} />
          </div>
        </div>
      </div>

      <div className="list-container">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Adresse</th>
              <th>Téléphone</th>
              <th>Email</th>
              <th>Type de contact</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            { contacts.length === 0 ? (
              <tr key="none">
                <td colSpan="7" className="text-center py-4 text-gray-600 italic">
                  { isLoading ? <Spinner color='primary'/> : "Aucun contact trouvé" }
                </td>
              </tr>
            ) : (
              contacts.filter(contact => filters.archived ? !!contact.archived_at : !contact.archived_at).map((contact, index) => (
                <ContactItem contact={contact} 
                  key={index} 
                  isPendingAction={pendingActionIds.includes(contact.id)}
                  isAnimating={animatingContactIds.includes(contact.id)}
                  editClick={handleEditClick}
                  archiveClick={handleArchiveClick}
                  deleteClick={handleDeleteClick}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )} 

      <Modal
        open={isOpen}
        onClose={closeModal}
        ref={dialogRef}
        backdrop={false}
        className="max-w-full rounded-none"
      >
        <Modal.Header showCloseButton={false} />
        <Modal.Body>
          <ContactForm onSuccess={closeModal} onClose={closeModal}/>
        </Modal.Body>
      </Modal>

    </div>
  )
}